import React, { useEffect, useMemo, useState } from "react";
import { Heading, Button, Loading, DataTable, Select } from "@aidkitorg/component-library";
import { useParams, useHistory } from "react-router-dom";
import { usePost } from "../API";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import type { ColumnDef } from "@tanstack/react-table";
import type { Model as ScoreModel } from "@aidkitorg/aidkit/lib/convoai/schemas/Score";

type ScoreRow = Partial<Pick<ScoreModel, 'id'>> & Pick<ScoreModel, 'score_type' | 'reasoning'> & { value: number | null };

type ConversationScoringProps = {
  conversationId: string;
  onSaved?: () => void;
  onBack?: () => void;
  showHeading?: boolean;
};

const SCORE_OPTIONS = [1, 2, 3, 4, 5];
const DEFAULT_SCORE_TYPES = ["Clarity", "Accuracy", "Tone"];

export function ConversationScoring(props: ConversationScoringProps) {
  const addScores = usePost('/coach/conversation/add_scores');
  const getScores = usePost('/coach/conversation/scores');

  const [rows, setRows] = useState<ScoreRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  useAsyncEffect(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getScores({ conversationId: props.conversationId });
      if ('error' in resp) {
        setError(typeof resp.error === 'string' ? resp.error : 'Failed to load scores');
        setRows([]);
      } else if (Array.isArray(resp)) {
        const items = resp as ScoreModel[];
        const existing: ScoreRow[] = items.map((r) => ({ id: r.id, score_type: r.score_type, value: Number(r.value), reasoning: typeof r.reasoning === 'string' ? r.reasoning : '' }));
        const existingTypes = new Set(existing.map(r => r.score_type));
        const missing: ScoreRow[] = DEFAULT_SCORE_TYPES
          .filter(t => !existingTypes.has(t))
          .map(t => ({ score_type: t, value: null, reasoning: '' }));
        setRows([...existing, ...missing]);
      } else {
        setRows(DEFAULT_SCORE_TYPES.map(t => ({ score_type: t, value: null, reasoning: '' })));
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load scores');
      setRows(DEFAULT_SCORE_TYPES.map(t => ({ score_type: t, value: null, reasoning: '' })));
    } finally {
      setLoading(false);
    }
  }, [props.conversationId]);

  const columns = useMemo<ColumnDef<ScoreRow>[]>(() => {
    const BASE_COL_SIZE = 140;
    return [
      { header: 'Score Type', accessorKey: 'score_type', size: BASE_COL_SIZE },
      { header: 'Value', id: 'value', size: BASE_COL_SIZE, cell: ({ row }: { row: { index: number, original: ScoreRow } }) => (
        <Select
          label=""
          description=""
          hideLabel={true}
          className={row.original.value === null ? 'border-red-300 bg-red-50' : ''}
          value={(() => {
            const options = [
              { value: '', label: '-- Select Score --' },
              ...SCORE_OPTIONS.map(v => ({ value: v.toString(), label: v.toString() }))
            ];
            return options.find(opt => opt.value === (row.original.value?.toString() ?? ''));
          })()}
          onChange={(selectedOption) => {
            setRows(prev => {
              const next = [...prev];
              next[row.index] = { ...next[row.index], value: selectedOption?.value === '' ? null : Number(selectedOption?.value) };
              return next;
            });
          }}
          options={[
            { value: '', label: '-- Select Score --' },
            ...SCORE_OPTIONS.map(v => ({ value: v.toString(), label: v.toString() }))
          ]}
        />
      )
      },
      { header: 'Reasoning', id: 'reasoning', size: BASE_COL_SIZE * 3, cell: ({ row }: { row: { index: number, original: ScoreRow } }) => (
        <textarea
          className="w-full border rounded px-3 py-2 resize-y"
          rows={3}
          value={row.original.reasoning}
          onChange={(e) => {
            setRows(prev => {
              const next = [...prev];
              next[row.index] = { ...next[row.index], reasoning: e.target.value };
              return next;
            });
          }}
          placeholder="Briefly explain your score"
        />
      )
      }
    ];
  }, []);

  async function onSave() {
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    // Validate that all scores have been set
    const unscored = rows.filter(r => r.value === null || r.value === undefined);
    if (unscored.length > 0) {
      setError(`Please provide scores for all criteria: ${unscored.map(r => r.score_type).join(', ')}`);
      setSaving(false);
      return;
    }
    
    try {
      const payload = {
        scores: rows.map(r => ({
          score_type: r.score_type,
          value: Number(r.value!), // Safe to use ! because we validated above
          reasoning: r.reasoning,
          conversation_id: props.conversationId,
          message_ids: null
        }))
      };
      const resp = await addScores(payload);
      if ('error' in resp) {
        setError(typeof resp.error === 'string' ? resp.error : 'Failed to save scores');
      } else {
        setSuccess(true);
        if (props.onSaved) setTimeout(() => props.onSaved?.(), 600);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save scores');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4">
      {(props.showHeading ?? true) && (
        <>
          <Heading level="h1">Score Conversation</Heading>
          <p className="mt-2">Conversation ID: <code>{props.conversationId}</code></p>
        </>
      )}

      {loading ? (
        <Loading loading={true} />
      ) : (
        <>
          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
          {success && <p className="text-green-700 text-sm mt-3">Scores saved.</p>}

          <div className="mt-4">
            <DataTable
              pageSize={10}
              resizableColumns={false}
              enableSearch={false}
              columns={columns}
              data={rows}
            />
          </div>

          <div className="flex items-center gap-3 mt-4">
            <Button onClick={onSave} disabled={saving}>{saving ? <Loading loading={true} /> : 'Save All Scores'}</Button>
            {props.onBack && <Button variant="secondary" onClick={props.onBack}>Back to Review Queue</Button>}
          </div>
        </>
      )}
    </div>
  );
}

export function ConversationScoringPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const history = useHistory();
  return (
    <ConversationScoring
      conversationId={conversationId}
      onSaved={() => history.push('/ai/config/review')}
      onBack={() => history.push('/ai/config/review')}
      showHeading={true}
    />
  );
} 